import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/presentation/providers/auth.provider";
import { useCurrentProfile } from "@/application/use-cases/profile/use-profile.queries";
import { Workspace, isWorkspaceId, type WorkspaceId } from "@/presentation/data/workspaces";

const STORAGE_KEY = "yango:workspace";

interface WorkspaceContextValue {
  /** the workspace the user is currently viewing */
  currentWorkspace: WorkspaceId;
  setWorkspace: (id: WorkspaceId) => void;
  /** workspaces this user is allowed to see (from their profile) */
  availableWorkspaces: WorkspaceId[];
  /** false → view-only (write controls disabled; RLS also rejects writes) */
  canWrite: boolean;
  /** true → may manage users (admin Users page); gated by profiles.is_admin */
  isAdmin: boolean;
  /** true while the profile is still loading */
  loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

function readStored(): WorkspaceId | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw && isWorkspaceId(raw) ? raw : null;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const profileQ = useCurrentProfile(session?.user?.id);
  const profile = profileQ.data ?? null;

  // Allowed workspaces come from the profile. Fail CLOSED: until a real profile
  // loads (loading / missing / errored) the user gets NO workspaces, so they see
  // no implementation access. A loaded profile is authoritative.
  const availableWorkspaces = useMemo<WorkspaceId[]>(() => {
    if (!profile) return [];
    return (profile.workspaces ?? []).filter(isWorkspaceId);
  }, [profile]);

  const [selected, setSelected] = useState<WorkspaceId | null>(() => readStored());

  // Keep `selected` inside the allowed set: if the user lost access to (or never
  // had) the stored workspace, snap to the first one they can see. With no
  // allowed workspaces yet (fail-closed default), fall back to assessment purely
  // as a display value — access is gated on `availableWorkspaces`, not this.
  const currentWorkspace = useMemo<WorkspaceId>(() => {
    if (selected && availableWorkspaces.includes(selected)) return selected;
    return availableWorkspaces[0] ?? Workspace.ASSESSMENT;
  }, [selected, availableWorkspaces]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, currentWorkspace);
  }, [currentWorkspace]);

  const setWorkspace = useCallback((id: WorkspaceId) => {
    setSelected(id);
  }, []);

  // Fail CLOSED: no write access until a real profile loads. A loaded profile is
  // authoritative; RLS is the backstop for writes either way.
  const canWrite = profile ? profile.can_write : false;
  // Admin capability (Users page). Fail closed until a real profile loads.
  const isAdmin = profile ? profile.is_admin === true : false;
  // Only "still fetching" counts as loading. A signed-in user whose profile query
  // has settled with no row is resolved (least-privilege: empty workspaces,
  // canWrite=false) — the guard then redirects them out of any workspace route.
  const loading = !!session && profileQ.isLoading;

  const value: WorkspaceContextValue = {
    currentWorkspace,
    setWorkspace,
    availableWorkspaces,
    canWrite,
    isAdmin,
    loading,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return ctx;
}
